import { ComponentPropsWithoutRef, ReactNode } from 'react';

import classNames from 'classnames';

import ImageProxy from '@next-image-proxy';

import { Button, ExternalLink } from '@uikit';

import { ProjectContentTableOfContentTab } from '../types';
import { ItemsProps } from '../types';
import {
  businessModel,
  howToParticipate,
  keyFeatures,
  marketTraction,
  partners,
  roadmap,
  tokenUtility,
} from './constants';

export interface SubTitleProps extends ComponentPropsWithoutRef<'h3'> {
  children: ReactNode;
}

export interface ListProps extends ComponentPropsWithoutRef<'ul'> {
  items: Array<ItemsProps>;
  description?: string;
  descriptionClassName?: string;
  withNumericSymbols?: boolean;
}

const List = ({
  items,
  description,
  className,
  descriptionClassName,
  withNumericSymbols,
}: ListProps) => {
  return (
    <>
      {Boolean(description) && (
        <p
          className={classNames(
            'mb-4 sm:text-2xl font-semibold text-neutral-500',
            descriptionClassName,
          )}
        >
          {description}
        </p>
      )}
      <ul
        className={classNames(
          'pl-6',
          {
            'list-decimal': withNumericSymbols,
            'list-disc': !withNumericSymbols,
          },
          className,
        )}
      >
        {items.map(({ id, content, item }) => {
          if (Array.isArray(item)) {
            return (
              <li key={id} className="mb-4">
                {content}
                <ul className="list-disc pl-6">
                  {item.map(({ id: subId, subItem }) => (
                    <li key={subId}>{subItem}</li>
                  ))}
                </ul>
              </li>
            );
          } else {
            return (
              <li className="max-sm:text-sm" key={id}>
                {item}
              </li>
            );
          }
        })}
      </ul>
    </>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'how-to-participate',
    title: 'How to Participate',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm font-normal">
          To join the LiveArt Private Sale on the Magic Launchpad, follow these steps:
        </p>
        <List items={howToParticipate} withNumericSymbols />
        <p className="mt-4 max-sm:text-sm font-normal">
          The Private Sale is available to all Magic Launchpad users and will be conducted as a
          Fair-Share (Pro-Rata) Sale. In this system, your allocation is determined by the size of
          your deposit relative to the total deposits in the pool — the larger your deposit, the
          bigger your allocation.
        </p>
        <p className="mt-4 max-sm:text-sm font-normal">
          After the sale ends, any unused portion of your deposit will be refunded automatically.
        </p>
        <p className="mt-4 max-sm:text-sm font-normal">
          For an even greater allocation, you can activate the&nbsp;
          <ExternalLink
            href="https://docs.magicsquare.io/magic-launchpad/key-features#turbo-charge-feature"
            className="text-base-text-blue underline"
          >
            Turbo-Charge
          </ExternalLink>
          &nbsp;feature, which boosts your potential allocation by up to 500%!
        </p>
      </>
    ),
  },
  {
    id: 'introduction',
    title: 'Introduction',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          Supported by industry-leading investors such as{' '}
          <b>Binance Labs, Hashkey Capital, Animoca Brands, and Samsung Ventures</b>, LiveArt is
          redefining art as an asset class, using blockchain technology to open up new avenues of
          ownership and engagement. With a Web3-native team behind multiple IPOs and major Web2
          exits (including Viacom, Christie's, and Sotheby's), LiveArt provides an innovative
          platform for traditional and digital art investments. Through blockchain, AI analytics,
          and a tokenized ecosystem, LiveArt enables both new and seasoned investors to experience
          art like never before, making it a tool for both enjoyment and financial growth.
        </p>
      </>
    ),
  },
  {
    id: 'key-features',
    title: 'Key Features',
    content: <List items={keyFeatures} />,
  },
  {
    id: 'achievements-market-traction',
    title: 'Achievements / Market Traction',
    content: <List items={marketTraction} />,
  },
  {
    id: 'problem-solution',
    title: 'Problem and Solution',
    content: (
      <p className="font-normal max-sm:text-sm">
        Traditionally, art investments have been exclusive to affluent collectors due to high costs
        and limited accessibility. This exclusivity has excluded a broader audience and created
        barriers to entry in the art market. LiveArt addresses these challenges by using blockchain
        to offer fractional ownership of high-value physical artworks, thus lowering the financial
        barrier. Through AI-driven analytics and transparency-enhancing tools, the platform provides
        a level of insight rarely available in the art world. Gamified engagement features make the
        platform accessible and enjoyable for all, while blockchain-enabled transparency and
        ownership provide a safe and innovative way to democratize art.
      </p>
    ),
  },
  {
    id: 'investors-backers',
    title: 'Investors/Backers',
    content: (
      <>
        <p className="font-normal max-sm:text-sm mb-10">
          LiveArt is backed by prominent investors and key stakeholders in the blockchain and
          finance industry, including:
        </p>
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-[5px] mb-[5px]">
          {partners.slice(0, 3).map(({ name, image, width, height, className }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-[5px]">
          {partners.slice(3, 5).map(({ name, image, width, height, className }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'business-model',
    title: 'Business Model',
    content: (
      <>
        <p className="font-semibold max-sm:text-sm mb-4">
          LiveArt operates on a multi-faceted revenue model designed for sustainable growth and
          diversification:
        </p>
        <List items={businessModel} />
      </>
    ),
  },
  {
    id: 'token-utility',
    title: 'Token Utility',
    content: (
      <>
        <p className="font-semibold max-sm:text-sm mb-4">
          The $ART token fuels the LiveArt ecosystem and provides holders with exclusive benefits:
        </p>
        <List items={tokenUtility} />
        <ImageProxy
          alt="Token utility"
          src="/launchpad/stories/liveart/token-utility.png.png"
          className="block w-full h-auto mt-4 object-contain"
          quality={80}
          width={773}
          height={342}
        />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink className="underline" href="https://docs.liveart.xyz/web3/art-token/intro">
            Learn More
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'tokenomics',
    title: 'Tokenomics',
    content: (
      <>
        <ImageProxy
          alt="Tokenomics diagram"
          src="/launchpad/stories/liveart/tokenomics.png"
          className="block w-full h-auto mt-4 rounded-lg object-contain"
          quality={80}
          width={773}
          height={403}
        />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink
            className="underline"
            href="https://docs.liveart.xyz/web3/art-token/tokenomics"
          >
            Learn More
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'roadmap',
    title: 'Roadmap',
    content: <List items={roadmap} />,
  },
];
